// components/dashboard/ExportToExcel.jsx
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button, message, Tooltip } from "antd";
import { FaFileExport } from "react-icons/fa";

export default function ExportToExcel({ data, loading }) {
  const exportFile = async () => {
    if (!data) {
      message.warning("No hay datos para exportar");
      return;
    }

    const hasPersonas = data.personas && data.personas.length > 0;
    const hasLlamadas = data.llamadas && data.llamadas.length > 0;

    if (!hasPersonas && !hasLlamadas) {
      message.warning("No hay datos para exportar");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Sistema 724";
      workbook.created = new Date();

      const headerStyle = {
        font: { bold: true, color: { argb: "FFFFFFFF" }, size: 11 },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E5A8E" } },
        alignment: { horizontal: "center", vertical: "middle", wrapText: true },
        border: {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        },
      };

      const cellStyle = {
        alignment: { vertical: "middle", wrapText: true },
        border: {
          top: { style: "thin", color: { argb: "FFD3D3D3" } },
          left: { style: "thin", color: { argb: "FFD3D3D3" } },
          bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
          right: { style: "thin", color: { argb: "FFD3D3D3" } },
        },
      };

      // 1. HOJA: PERSONAS (ACTUALIZADA CON LOS CAMPOS CORRECTOS)
      if (hasPersonas) {
        const sheet = workbook.addWorksheet("Personas");
        sheet.columns = [
          // Identificación básica
          { header: "ID Participante", key: "id_participante", width: 12 },
          { header: "ID Registro", key: "id_registro", width: 12 },
          { header: "Nombres", key: "nombres", width: 20 },
          { header: "Apellidos", key: "apellidos", width: 20 },
          { header: "Nombre Completo", key: "nombre_completo", width: 30 },

          // Documento
          { header: "Tipo Documento Código", key: "tipo_documento_codigo", width: 15 },
          { header: "Tipo Documento", key: "tipo_documento_nombre", width: 18 },
          { header: "Número Documento", key: "num_identificacion", width: 18 },
          { header: "Fecha Expedición", key: "fecha_expedicion", width: 15 },
          { header: "Fecha Corte RNEC", key: "fecha_corte_rnec", width: 15 },

          // Género y edad
          { header: "Género Código", key: "genero_codigo", width: 10 },
          { header: "Género", key: "genero_nombre", width: 12 },
          { header: "Fecha Nacimiento", key: "fecha_nacimiento", width: 15 },
          { header: "Edad", key: "edad", width: 8 },

          // Contacto
          { header: "Correo Electrónico", key: "correo", width: 25 },
          { header: "Celular", key: "celular", width: 15 },
          { header: "Teléfono Fijo Actual", key: "telefono_fijo_actual", width: 15 },
          { header: "Celular Actual RUV", key: "celular_act_ruv", width: 15 },
          { header: "Correo Actual RUV", key: "correo_act_ruv", width: 25 },

          // Ubicación
          { header: "Departamento", key: "departamento", width: 20 },
          { header: "Municipio", key: "municipio", width: 20 },
          { header: "Corregimiento", key: "corregimiento_nombre", width: 20 },
          { header: "Barrio", key: "barrio_nombre", width: 20 },
          { header: "Vereda", key: "vereda_nombre", width: 20 },
          { header: "Zona", key: "zona_nombre", width: 15 },
          { header: "Dirección", key: "direccion", width: 25 },
          { header: "Dirección Actual RUV", key: "direccion_actual_ruv", width: 25 },

          // Estados y validación
          { header: "Estado Participante", key: "estado_participante_nombre", width: 18 },
          { header: "Total Intentos", key: "total_intentos", width: 12 },
          { header: "Estado Validación", key: "estado_validacion_nombre", width: 18 },

          // Metadatos
          { header: "Orden Prioridad", key: "orden_prioridad", width: 12 },
          { header: "Focalizado HC2024", key: "focalizado_hc2024", width: 15 },
          { header: "Fecha Creación", key: "created_at", width: 18 },
        ];

        data.personas.forEach((p) => {
          sheet.addRow({
            // Identificación básica
            id_participante: p.id_participante || '',
            id_registro: p.id_registro || '',
            nombres: p.nombres || '',
            apellidos: p.apellidos || '',
            nombre_completo: p.nombre_completo || '',

            // Documento
            tipo_documento_codigo: p.tipo_documento_codigo || '',
            tipo_documento_nombre: p.tipo_documento_nombre || '',
            num_identificacion: p.num_identificacion || '',
            fecha_expedicion: p.fecha_expedicion ? new Date(p.fecha_expedicion).toLocaleDateString('es-ES') : '',
            fecha_corte_rnec: p.fecha_corte_rnec ? new Date(p.fecha_corte_rnec).toLocaleDateString('es-ES') : '',

            // Género y edad
            genero_codigo: p.genero_codigo || '',
            genero_nombre: p.genero_nombre || '',
            fecha_nacimiento: p.fecha_nacimiento ? new Date(p.fecha_nacimiento).toLocaleDateString('es-ES') : '',
            edad: p.edad || '',

            // Contacto
            correo: p.correo || '',
            celular: p.celular || '',
            telefono_fijo_actual: p.telefono_fijo_actual || '',
            celular_act_ruv: p.celular_act_ruv || '',
            correo_act_ruv: p.correo_act_ruv || '',

            // Ubicación
            departamento: p.departamento || '',
            municipio: p.municipio || '',
            corregimiento_nombre: p.corregimiento_nombre || '',
            barrio_nombre: p.barrio_nombre || '',
            vereda_nombre: p.vereda_nombre || '',
            zona_nombre: p.zona_nombre || '',
            direccion: p.direccion || '',
            direccion_actual_ruv: p.direccion_actual_ruv || '',

            // Estados y validación
            estado_participante_nombre: p.estado_participante_nombre || '',
            total_intentos: p.total_intentos || 0,
            estado_validacion_nombre: p.estado_validacion_nombre || '',

            // Metadatos
            orden_prioridad: p.orden_prioridad || '',
            focalizado_hc2024: p.focalizado_hc2024 === 1 ? 'Sí' : 'No',
            created_at: p.created_at ? new Date(p.created_at).toLocaleString('es-ES') : '',
          });
        });

        sheet.getRow(1).eachCell((cell) => Object.assign(cell, headerStyle));
        for (let i = 2; i <= sheet.rowCount; i++) {
          sheet.getRow(i).eachCell((cell) => Object.assign(cell, cellStyle));
        }
        sheet.views = [{ state: 'frozen', ySplit: 1 }];
      }

      // 2. HOJA: LLAMADAS (ACTUALIZADA CON LOS CAMPOS CORRECTOS)
      if (hasLlamadas) {
        const sheet = workbook.addWorksheet("Llamadas");
        sheet.columns = [
          { header: "ID Llamada", key: "id", width: 12 },
          { header: "ID Participante", key: "participante_id", width: 12 },
          { header: "Nombre Participante", key: "nombre_participante", width: 30 },
          { header: "Número Identificación", key: "num_identificacion", width: 18 },
          { header: "Departamento", key: "departamento", width: 20 },
          { header: "Municipio", key: "municipio", width: 20 },
          { header: "Estado Llamada", key: "estado_llamada", width: 20 },
          { header: "Número Intento", key: "numero_intento", width: 12 },
          { header: "Fecha Creación", key: "created_at", width: 18 },
          { header: "Duración (segundos)", key: "duracion_llamada", width: 15 },
          { header: "Es Elegible", key: "es_elegible", width: 12 },
        ];

        data.llamadas.forEach((l) => {
          sheet.addRow({
            id: l.id || '',
            participante_id: l.participante_id || '',
            nombre_participante: l.nombre_participante || '',
            num_identificacion: l.num_identificacion || '',
            departamento: l.departamento || '',
            municipio: l.municipio || '',
            estado_llamada: l.estado_llamada || '',
            numero_intento: l.numero_intento || '',
            created_at: l.created_at ? new Date(l.created_at).toLocaleString('es-ES') : '',
            duracion_llamada: l.duracion_llamada || '',
            es_elegible: l.es_elegible === 1 ? 'Sí' : l.es_elegible === 0 ? 'No' : 'No aplica',
          });
        });

        sheet.getRow(1).eachCell((cell) => Object.assign(cell, headerStyle));
        for (let i = 2; i <= sheet.rowCount; i++) {
          sheet.getRow(i).eachCell((cell) => Object.assign(cell, cellStyle));
        }
        sheet.views = [{ state: 'frozen', ySplit: 1 }];
      }

      // 3. HOJA: RESUMEN (ACTUALIZADA)
      if (data.resumen && Object.keys(data.resumen).length > 0) {
        const sheet = workbook.addWorksheet("Resumen");

        const resumenData = [
          ["MÉTRICA", "VALOR"],
          ["Total Participantes", data.resumen.total_participantes || 0],
          ["Total Verificados", data.resumen.total_verificados || 0],
          ["Total En Proceso", data.resumen.total_en_proceso || 0],
          ["Total No Continúa", data.resumen.total_no_continua || 0],
          ["Total No Verificados", data.resumen.total_no_verificados || 0],
          ["Total Registros", data.resumen.total_registros || 0],
          ["Porcentaje Verificados", data.resumen.porcentaje_verificados ? `${data.resumen.porcentaje_verificados}%` : "0%"],
        ];

        sheet.columns = [
          { header: "Métrica", key: "metrica", width: 30 },
          { header: "Valor", key: "valor", width: 20 },
        ];

        resumenData.forEach((row, index) => {
          if (index === 0) {
            sheet.addRow({ metrica: row[0], valor: row[1] });
          } else {
            sheet.addRow({ metrica: row[0], valor: row[1] });
          }
        });

        sheet.getRow(1).eachCell((cell) => Object.assign(cell, headerStyle));
        for (let i = 2; i <= sheet.rowCount; i++) {
          sheet.getRow(i).eachCell((cell) => Object.assign(cell, cellStyle));
        }
      }

      // 4. HOJA: FILTROS APLICADOS
      if (data.metadata?.filtros_aplicados) {
        const sheet = workbook.addWorksheet("Filtros Aplicados");

        const filtrosArray = [];
        filtrosArray.push(["FILTROS APLICADOS", ""]);
        filtrosArray.push(["Fecha de Generación", data.metadata.fecha_generacion ? new Date(data.metadata.fecha_generacion).toLocaleString('es-ES') : new Date().toLocaleString('es-ES')]);
        filtrosArray.push(["", ""]);

        if (data.metadata.filtros_aplicados) {
          Object.entries(data.metadata.filtros_aplicados).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              // Formatear nombres de campos para mejor legibilidad
              let formattedKey = key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, (str) => str.toUpperCase())
                .replace(/_/g, ' ');

              let formattedValue = value;
              if (key.includes('fecha') && value) {
                formattedValue = new Date(value).toLocaleDateString('es-ES');
              }

              filtrosArray.push([formattedKey, formattedValue]);
            }
          });
        }

        sheet.columns = [
          { header: "Parámetro", key: "parametro", width: 30 },
          { header: "Valor", key: "valor", width: 30 },
        ];

        filtrosArray.forEach((row) => {
          sheet.addRow({ parametro: row[0], valor: row[1] });
        });

        sheet.getRow(1).eachCell((cell) => Object.assign(cell, headerStyle));
        for (let i = 2; i <= sheet.rowCount; i++) {
          sheet.getRow(i).eachCell((cell) => Object.assign(cell, cellStyle));
        }
      }

      // Generar nombre del archivo con fecha y hora
      const fechaActual = new Date();
      const fechaFormato = `${fechaActual.getFullYear()}${String(fechaActual.getMonth() + 1).padStart(2, '0')}${String(fechaActual.getDate()).padStart(2, '0')}_${String(fechaActual.getHours()).padStart(2, '0')}${String(fechaActual.getMinutes()).padStart(2, '0')}`;
      const fileName = `exportacion_sistema_724_${fechaFormato}.xlsx`;

      // Generar y descargar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        fileName
      );

      message.success("Archivo exportado exitosamente");

    } catch (error) {
      console.error("Error al exportar:", error);
      message.error("Error al generar el archivo Excel");
    }
  };

  return (
    <Tooltip title="Exportar datos a Excel">
      <Button
        type="primary"
        onClick={exportFile}
        disabled={!data || loading}
        loading={loading}
        icon={<FaFileExport />}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#2E5A8E',
          borderColor: '#2E5A8E'
        }}
      >
        Exportar a Excel
      </Button>
    </Tooltip>
  );
}